import { useMemo } from 'react';
import { Inbox, MailCheck } from 'lucide-react';
import { Badge, Button, Card, EmptyState, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import {
  useMarkAllNotificationsRead,
  useUnreadNotifications,
} from '../../notification/model/notification.queries.js';
import { useOrgUnits } from '../../org/model/org.queries.js';
import { useWarehouses } from '../../warehouse/model/warehouse.queries.js';
import { useInbox } from '../model/requisition.queries.js';
import { requisitionErrorText } from '../model/requisition-error.js';
import { AcceptQueue } from './AcceptQueue.js';
import { RequisitionTable } from './RequisitionTable.js';

export interface InboxPageProps {
  /** Kartochkaga o'tish — sahifa marshrutlashni bilmaydi. */
  onOpen?: (requisitionId: string) => void;
}

/**
 * "MENING NAVBATIM" — xodim HOZIR ish qila oladigan talabnomalar.
 *
 * Ro'yxatni SERVER yig'adi va u ruxsatlardan hisoblanadi: ruxsati yo'q xodim
 * bo'sh ro'yxat oladi, 403 emas. Shuning uchun bu ekranda hech qanday ruxsat
 * tekshiruvi yo'q — "navbatim bo'shmi yoki menga ruxsat yo'qmi" degan farq
 * foydalanuvchi uchun ahamiyatsiz.
 *
 * Ro'yxat o'zi yangilanadi (`refetchInterval`): talabnoma navbatga BOSHQA
 * xodimning amalidan tushadi, ya'ni sahifada hech qanday mahalliy hodisa
 * bo'lmaydi.
 */
export const InboxPage = ({ onOpen }: InboxPageProps) => {
  const inbox = useInbox();
  const warehouses = useWarehouses({ limit: 200 });
  const unread = useUnreadNotifications();
  const markAllRead = useMarkAllNotificationsRead();
  /* Bo'lim nomi FAQAT telefon kartochkasida ko'rinadi, lekin so'rov shu
     yerda: ro'yxat bitta va u ikkala ko'rinishga ham xizmat qiladi.
     Javob 5 daqiqa keshda yotadi (`staleTime`), ya'ni sahifadan sahifaga
     o'tganda qayta so'ralmaydi. */
  const orgUnits = useOrgUnits();

  const warehouseNames = useMemo(
    () => new Map((warehouses.data?.items ?? []).map((w) => [w.id, w.name])),
    [warehouses.data],
  );

  const items = inbox.data?.items ?? [];
  const unreadCount = unread.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            {t('requisition.inboxTitle')}
            {items.length === 0 ? null : (
              <Badge tone="info" mono>
                {items.length}
              </Badge>
            )}
          </span>
        }
        description={t('requisition.inboxSubtitle')}
        actions={
          unreadCount === 0 ? undefined : (
            <Button
              variant="secondary"
              loading={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              <MailCheck size={16} aria-hidden />
              {t('requisition.inboxMarkAllRead')}
            </Button>
          )
        }
      />

      {inbox.isError ? (
        <Card>
          <EmptyState
            title={t('requisition.inboxError')}
            description={requisitionErrorText(inbox.error, '')}
            action={
              <Button variant="secondary" onClick={() => void inbox.refetch()}>
                {t('requisition.retry')}
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          {/* Yuklanish va bo'sh holat ham JADVALGA topshiriladi: skelet
              telefonda kartochka, keng ekranda jadval shaklida bo'lishi
              kerak va bu qaror bitta joyda — ko'rinishni biladigan
              komponentda — qolishi lozim. */}
          <RequisitionTable
            caption={t('requisition.inboxTitle')}
            rows={items}
            pending={inbox.isPending}
            warehouseNames={warehouseNames}
            orgUnitName={orgUnits.shortNameOf}
            empty={
              <EmptyState
                icon={<Inbox size={20} aria-hidden />}
                title={t('requisition.inboxEmpty')}
                description={t('requisition.inboxEmptyHint')}
              />
            }
            {...(onOpen === undefined ? {} : { onOpen: (row) => onOpen(row.id) })}
          />
          {inbox.isPending || items.length === 0 ? null : (
            <p className={cn('tabular text-xs text-text-faint')}>
              {t('requisition.inboxCount', { count: inbox.data?.total ?? items.length })}
            </p>
          )}
        </>
      )}

      {/* Suzuvchi tugma, oyna va ular egallagan bo'shliq — bittada.
          Ayni shu element boshqaruv panelida ham turadi. */}
      <AcceptQueue onOpenRequisition={(id) => onOpen?.(id)} />
    </div>
  );
};
